import * as React from 'react';
import { BLOCKS, Document, INLINES, Node } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Divider } from '@zoopla-eng/zds/divider';
import { removeSearchIdentifierFromLinkNode } from './functions/removeSearchIdentifierFromLinkNode';
import { renderHeadingOrJumpLink } from './functions/renderHeadingOrJumpLink';
import { renderMarkup } from './functions/helpers';
import EmbeddedEntry from './components/EmbeddedEntry';
import EntryHyperlink from './components/EntryHyperlink';
import Hyperlink from './components/Hyperlink';
import { BlockQuote } from '@zoopla-eng/zds/quote';
import { Link } from '@zoopla-eng/zds/link';
import { Box } from '@zoopla-eng/zds/box';
import {
    listStyle,
    orderedListStyle,
    textStyle
} from './ContentfulRenderOptions.styles.css';
import { PageTitleShort } from '@zoopla-eng/zds/headings';
import { fontGroups } from '@zoopla-eng/zds/themes';

// Defines commonly shared Contentful rich text options used
// when rendering rich text fields
// e.g. shared between Article rich text and Author Bio rich text fields
const OPTIONS = {
    [BLOCKS.HEADING_1]: (_node: Node, children: React.ReactNode) => (
        <PageTitleShort>{children}</PageTitleShort>
    ),
    [BLOCKS.HEADING_2]: (_node: Node, children: React.ReactNode) => {
        return renderHeadingOrJumpLink(_node, children);
    },
    [BLOCKS.HEADING_3]: (_node: Node, children: React.ReactNode) => {
        return renderHeadingOrJumpLink(_node, children);
    },
    [BLOCKS.HEADING_4]: (_node: Node, children: React.ReactNode) => (
        <h4 className={fontGroups.plate}>{children}</h4>
    ),
    [BLOCKS.PARAGRAPH]: (_node: Node, children: React.ReactNode) =>
        children && (
            <p className={`${textStyle} ${fontGroups.ladle}`}>{children}</p>
        ),
    [BLOCKS.UL_LIST]: (_node: Node, children: React.ReactNode) => (
        // <Bulleted items={children as ReactElement[]} />
        <ul className={`${listStyle} ${fontGroups.ladle}`}>{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: Node, children: React.ReactNode) => (
        <ol className={`${orderedListStyle} ${fontGroups.ladle}`}>
            {children}
        </ol>
    ),
    [BLOCKS.QUOTE]: (_node: Node, children: React.ReactNode) =>
        documentToReactComponents(_node as Document, {
            renderNode: {
                [BLOCKS.QUOTE]: (_node, children) => (
                    <BlockQuote>{children}</BlockQuote>
                )
            },
            renderText: (text: string) => {
                return text
                    .split('\n')
                    .reduce(
                        (
                            children: string | React.ReactNode[],
                            textSegment: string,
                            index: number
                        ) => {
                            return [
                                ...children,
                                index > 0 && <br key={index} />,
                                textSegment
                            ];
                        },
                        []
                    );
            }
        }),
    [INLINES.HYPERLINK]: (node: Node) => (
        <Hyperlink nodeData={removeSearchIdentifierFromLinkNode(node)} />
    ),
    [BLOCKS.HR]: () => (
        <Box pb="M100">
            <Divider direction="horizontal" />
        </Box>
    ),
    [INLINES.ENTRY_HYPERLINK]: (node: Node) => (
        <EntryHyperlink nodeData={node} />
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
        return <EmbeddedEntry nodeData={node} />;
    },
    [INLINES.ASSET_HYPERLINK]: (node: Node, children: React.ReactNode) => {
        let url = '';
        if (node.data['target'].fields) {
            url = node.data['target'].fields.file.url;
        } else {
            // We throw a warning just in case the user is in preview mode
            // which means the asset might not yet be published (because preview mode)
            // and we don't want to break the page
            console.warn(
                'An asset hyperlink is undefined -- has the asset been published?'
            );
        }
        return <Link href={url}>{children}</Link>;
    }
};

export const CONTENTFUL_RICH_TEXT_OPTIONS = {
    renderNode: {
        ...OPTIONS
    },
    renderMarkup
};
